import { useEffect, useState, useMemo } from 'react';
import clsx from 'clsx';
import {
  Activity,
  Search,
  RefreshCw,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Filter,
  User,
} from 'lucide-react';
import { adminApi } from '../../../api/client';
import type { OrgAuditEntry } from '../../../api/client';
import { usePermissions } from '../../../hooks/usePermissions';

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatDatetime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ── Action badge ───────────────────────────────────────────────────────────────

const ACTION_STYLES: Record<string, string> = {
  'member.invited':                    'text-brand-light bg-brand/10 border-brand/20',
  'member.removed':                    'text-loss bg-loss/10 border-loss/20',
  'member.role_changed':               'text-warn bg-warn/10 border-warn/20',
  'invitation.revoked':                'text-loss bg-loss/10 border-loss/20',
  'invitation.resent':                 'text-brand-light bg-brand/10 border-brand/20',
  'organization.ownership_transferred': 'text-warn bg-warn/10 border-warn/20',
  'organization.updated':              'text-brand bg-brand/10 border-brand/20',
};

function ActionBadge({ action }: { action: string }) {
  const style = ACTION_STYLES[action] ?? 'text-gray-400 bg-gray-400/10 border-gray-400/20';
  return (
    <span
      className={clsx(
        'inline-flex items-center text-[10px] font-semibold tracking-wide border rounded px-1.5 py-0.5',
        style,
      )}
    >
      {action}
    </span>
  );
}

// ── JSON colorizer ─────────────────────────────────────────────────────────────

function ColorizedJSON({ value }: { value: unknown }) {
  const raw = JSON.stringify(value, null, 0);
  const parts: React.ReactNode[] = [];
  let i = 0;
  const str = raw;
  // Simple token-by-token render
  const keyRe = /"([^"\\]*(\\.[^"\\]*)*)"(?=\s*:)/g;
  const strRe = /(?<!:)\s*"([^"\\]*(\\.[^"\\]*)*)"/g;
  const numRe = /\b(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)\b/g;
  // Split the string into tokens
  const tokens: { text: string; cls: string }[] = [];
  let last = 0;
  const combined = str.replace(
    /("(?:[^"\\]|\\.)*")(\s*:)?|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|([{}\[\],])/g,
    (match, str1, colon, num, punct, offset) => {
      if (last < offset) tokens.push({ text: str.slice(last, offset), cls: 'text-gray-600' });
      if (str1 && colon) {
        tokens.push({ text: str1, cls: 'text-brand-light' }); // key
        tokens.push({ text: colon, cls: 'text-gray-600' });
      } else if (str1) {
        tokens.push({ text: str1, cls: 'text-win' }); // string value
      } else if (num) {
        tokens.push({ text: num, cls: 'text-warn' }); // number
      } else if (punct) {
        tokens.push({ text: punct, cls: 'text-gray-600' });
      }
      last = offset + match.length;
      return '';
    },
  );
  if (last < str.length) tokens.push({ text: str.slice(last), cls: 'text-gray-600' });
  return (
    <span>
      {tokens.map((tok, i) => (
        <span key={i} className={tok.cls}>{tok.text}</span>
      ))}
    </span>
  );
}

// ── Detail panel ───────────────────────────────────────────────────────────────

function EntryDetail({ entry }: { entry: OrgAuditEntry }) {
  const hasData = entry.before !== null || entry.after !== null || entry.metadata !== null;
  if (!hasData) return null;
  return (
    <div className="mt-2 space-y-1 text-[11px] font-mono text-gray-500">
      {entry.before !== null && (
        <div>
          <span className="text-gray-600 mr-1">before:</span>
          <ColorizedJSON value={entry.before} />
        </div>
      )}
      {entry.after !== null && (
        <div>
          <span className="text-gray-600 mr-1">after:</span>
          <ColorizedJSON value={entry.after} />
        </div>
      )}
      {entry.metadata !== null && (
        <div>
          <span className="text-gray-600 mr-1">meta:</span>
          <ColorizedJSON value={entry.metadata} />
        </div>
      )}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

const PAGE_SIZE = 25;

const COMMON_ACTIONS = [
  'member.invited',
  'member.removed',
  'member.role_changed',
  'invitation.revoked',
  'invitation.resent',
  'organization.ownership_transferred',
  'organization.updated',
];

export function AdminAuditPage() {
  const { can } = usePermissions();
  const canView = can('audit_log.view');

  const [entries,   setEntries]   = useState<OrgAuditEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error,     setError]     = useState<string | null>(null);
  const [search,    setSearch]    = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [page,      setPage]      = useState(0);
  const [total,     setTotal]     = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const load = (pageIndex = 0, action = '') => {
    setIsLoading(true);
    adminApi
      .getAuditLog({ limit: PAGE_SIZE, offset: pageIndex * PAGE_SIZE, action: action || undefined })
      .then((data) => {
        setEntries(data);
        // Extract total from the response meta if available — fall back to data.length
        setTotal(data.length < PAGE_SIZE ? pageIndex * PAGE_SIZE + data.length : (pageIndex + 2) * PAGE_SIZE);
      })
      .catch((err) => setError((err as Error).message ?? 'Failed to load audit log'))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    if (!canView) return;
    load(page, actionFilter);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView, page, actionFilter]);

  const filtered = useMemo(() => {
    if (!search) return entries;
    const q = search.toLowerCase();
    return entries.filter(
      (e) =>
        e.action.toLowerCase().includes(q) ||
        (e.actorEmail ?? '').toLowerCase().includes(q) ||
        (e.actorName ?? '').toLowerCase().includes(q) ||
        (e.resourceType ?? '').toLowerCase().includes(q),
    );
  }, [entries, search]);

  const handlePageChange = (next: number) => {
    if (next < 0) return;
    setPage(next);
  };

  if (!canView) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-8">
        <p className="text-sm text-gray-500">You don't have permission to view audit activity.</p>
      </div>
    );
  }

  const hasNext = entries.length === PAGE_SIZE;
  const hasPrev = page > 0;

  return (
    <div className="max-w-5xl mx-auto px-6 py-8 space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-100">Audit Activity</h2>
        <p className="text-sm text-gray-500 mt-1">
          Organization audit trail — member changes, invitation activity, and admin actions.
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-3 p-3 bg-loss/10 border border-loss/20 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-loss flex-shrink-0" />
          <p className="text-sm text-loss">{error}</p>
          <button onClick={() => setError(null)} className="ml-auto text-loss/60 hover:text-loss" aria-label="Dismiss">×</button>
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-600 pointer-events-none" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search events…"
            className="w-full pl-8 pr-3 py-2 bg-surface-2 border border-surface-4 rounded-md text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:border-brand/60 transition-colors"
          />
        </div>
        <div className="relative">
          <Filter className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-600 pointer-events-none" />
          <select
            value={actionFilter}
            onChange={(e) => { setActionFilter(e.target.value); setPage(0); }}
            className="appearance-none pl-7 pr-3 py-2 bg-surface-2 border border-surface-4 rounded-md text-sm text-gray-100 focus:outline-none focus:border-brand/60 transition-colors"
          >
            <option value="">All actions</option>
            {COMMON_ACTIONS.map((a) => (
              <option key={a} value={a} className="bg-surface-2">{a}</option>
            ))}
          </select>
        </div>
        <button
          onClick={() => load(page, actionFilter)}
          className="p-2 text-gray-500 hover:text-gray-300 hover:bg-surface-3 rounded-md transition-colors"
          aria-label="Refresh"
        >
          <RefreshCw className={clsx('w-4 h-4', isLoading && 'animate-spin')} />
        </button>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-8 px-5">
            <RefreshCw className="w-4 h-4 animate-spin" />
            Loading audit log…
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center py-12 text-gray-500 gap-2">
            <Activity className="w-8 h-8 text-gray-700" />
            <p className="text-sm">No audit events found.</p>
          </div>
        ) : (
          <div className="divide-y divide-surface-4">
            {filtered.map((entry) => {
              const isExpanded = expandedId === entry.id;
              return (
                <div
                  key={entry.id}
                  className="px-4 py-3 hover:bg-surface-2/30 transition-colors cursor-pointer"
                  onClick={() => setExpandedId(isExpanded ? null : entry.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && setExpandedId(isExpanded ? null : entry.id)}
                  aria-expanded={isExpanded}
                >
                  <div className="flex items-start gap-3">
                    {/* Timestamp */}
                    <div className="flex-shrink-0 w-36 text-xs text-gray-500 pt-0.5">
                      {formatDatetime(entry.createdAt)}
                    </div>
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <ActionBadge action={entry.action} />
                        {entry.resourceType && (
                          <span className="text-xs text-gray-600">{entry.resourceType}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 mt-1">
                        <User className="w-3 h-3 text-gray-600 flex-shrink-0" />
                        <span className="text-xs text-gray-500 truncate">
                          {entry.actorName ?? entry.actorEmail ?? entry.actorId}
                        </span>
                      </div>
                      {isExpanded && <EntryDetail entry={entry} />}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      {!isLoading && entries.length > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-600">
            Page {page + 1} · {filtered.length} {filtered.length === 1 ? 'event' : 'events'}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(page - 1)}
              disabled={!hasPrev}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-gray-400 border border-surface-4 rounded-md hover:bg-surface-3 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              Prev
            </button>
            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={!hasNext}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-gray-400 border border-surface-4 rounded-md hover:bg-surface-3 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
